import { useState, useMemo } from 'react'
import { useContentUsage, type ContentUsageRow } from '@/api/hooks/usePages'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Search, Facebook, Instagram, Youtube, CheckCircle2,
  Clock, ClipboardList, LayoutGrid,
} from 'lucide-react'

const fmtDate = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })

function PlatformIcon({ platform }: { platform: string }) {
  switch (platform) {
    case 'fb': return <Facebook className="h-3.5 w-3.5 text-blue-500" />
    case 'ig': return <Instagram className="h-3.5 w-3.5 text-purple-500" />
    case 'yt': return <Youtube className="h-3.5 w-3.5 text-red-500" />
    default: return <span className="text-[10px]">{platform}</span>
  }
}

const thCls = 'text-[11px] font-semibold text-muted-foreground border-r border-border px-3 py-2 last:border-r-0 bg-muted/60'
const tdCls = 'text-[11px] text-foreground border-r border-border px-3 py-1.5 last:border-r-0'

export function ContentUsage() {
  const { t } = useLanguage()
  const { data: rows, isLoading } = useContentUsage()
  const [search, setSearch] = useState('')
  const [platformFilter, setPlatformFilter] = useState<string>('all')

  // Flat rows: one per page+folder (no grouping)
  const filteredRows = useMemo(() => {
    if (!rows) return []
    return rows
      .filter(r => {
        if (search) {
          const q = search.toLowerCase()
          if (!r.accountName.toLowerCase().includes(q) && !r.folderName.toLowerCase().includes(q)) return false
        }
        if (platformFilter !== 'all' && r.platform !== platformFilter) return false
        return true
      })
      .sort((a, b) => a.accountName.localeCompare(b.accountName))
  }, [rows, search, platformFilter])

  // KPIs
  const kpis = useMemo(() => {
    const uniquePages = new Set(filteredRows.map(r => r.pageId))
    const uniqueFolders = new Set(filteredRows.map(r => r.folderId))
    const pending = filteredRows.reduce((sum, r) => sum + r.pendingCount, 0)
    const published = filteredRows.reduce((sum, r) => sum + r.publishedCount, 0)
    return { pages: uniquePages.size, folders: uniqueFolders.size, pending, published }
  }, [filteredRows])

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <ClipboardList className="h-12 w-12 mb-3 opacity-20" />
        <p className="text-sm">{t('Nenhum conteudo agendado ainda')}</p>
        <p className="text-xs">{t('Agende pastas da biblioteca para ver o controle aqui')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Paginas ativas')}</p>
          <p className="text-xl font-bold">{kpis.pages}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Pastas utilizadas')}</p>
          <p className="text-xl font-bold">{kpis.folders}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Agendados')}</p>
          <p className="text-xl font-bold text-amber-500">{kpis.pending}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('Publicados')}</p>
          <p className="text-xl font-bold text-green-500">{kpis.published}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('Buscar pagina ou pasta...')}
            className="h-8 pl-8 text-xs"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'fb', 'ig', 'yt'].map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatformFilter(p)}
              className={cn(
                'flex items-center gap-1 rounded-md border px-2.5 py-1 text-[10px] font-medium transition-colors',
                platformFilter === p
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {p === 'all' ? <LayoutGrid className="h-3 w-3" /> : <PlatformIcon platform={p} />}
              {p === 'all' ? t('Todas') : p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Spreadsheet Table */}
      <div className="border border-border overflow-hidden">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className={cn(thCls, 'w-[180px]')}>{t('Pagina')}</th>
                <th className={cn(thCls, 'w-[110px]')}>{t('Conteudo')}</th>
                <th className={cn(thCls, 'w-[50px] text-center')}>{t('Rede')}</th>
                <th className={cn(thCls, 'min-w-[160px]')}>{t('Video')}</th>
                <th className={cn(thCls, 'w-[90px]')}>{t('Data agend.')}</th>
                <th className={cn(thCls, 'w-[120px]')}>{t('Ja programados')}</th>
                <th className={cn(thCls, 'w-[150px]')}>{t('Obs')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr
                  key={`${r.pageId}_${r.folderId}`}
                  className={cn(
                    'border-b border-border',
                    i % 2 === 1 && 'bg-muted/20',
                  )}
                >
                  {/* Página */}
                  <td className={cn(tdCls, 'font-medium')}>
                    <span className="block truncate max-w-[200px]" title={r.accountName}>
                      {r.accountName}
                    </span>
                  </td>

                  {/* Conteúdo (tipo) */}
                  <td className={tdCls}>
                    <span className="block truncate max-w-[100px] capitalize" title={r.contentType || ''}>
                      {r.contentType || '-'}
                    </span>
                  </td>

                  {/* Rede */}
                  <td className={cn(tdCls, 'text-center')}>
                    <PlatformIcon platform={r.platform} />
                  </td>

                  {/* Vídeo (pasta + contagem) */}
                  <td className={tdCls}>
                    <span className="block truncate max-w-[180px]" title={`${r.folderName} (${r.postCount})`}>
                      {r.folderName} <span className="text-muted-foreground">({r.postCount})</span>
                    </span>
                  </td>

                  {/* Data agend. */}
                  <td className={cn(tdCls, 'text-muted-foreground')}>
                    {r.lastScheduled ? fmtDate.format(new Date(r.lastScheduled)) : '-'}
                  </td>

                  {/* Já programados */}
                  <td className={tdCls}>
                    <div className="flex items-center gap-2">
                      {r.publishedCount > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400">
                          <CheckCircle2 className="h-3 w-3" />{r.publishedCount}
                        </span>
                      )}
                      {r.pendingCount > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-amber-500">
                          <Clock className="h-3 w-3" />{r.pendingCount}
                        </span>
                      )}
                      {r.publishedCount === 0 && r.pendingCount === 0 && (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </td>

                  {/* Obs */}
                  <td className={cn(tdCls, 'text-muted-foreground')}>
                    <span className="block truncate max-w-[150px]" title={r.notes || ''}>
                      {r.notes || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredRows.length === 0 && rows.length > 0 && (
        <div className="flex flex-col items-center py-8 text-muted-foreground">
          <Search className="h-6 w-6 mb-2 opacity-30" />
          <p className="text-xs">{t('Nenhum resultado para os filtros aplicados')}</p>
        </div>
      )}
    </div>
  )
}
